// src/pages/HomePage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Compass,
  Heart,
  Users,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Brain,
  Target,
  MessageSquare,
  Shield,
  Lightbulb,
  CheckCircle,
  Coffee,
  Star,
  Clock,
  BarChart2,
  Gift,
  BookOpen,
  Zap,
  Eye
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -50]);

  const realStats = [
    { number: "4", label: "In-Depth Assessments", icon: Brain },
    { number: "8", label: "Learning Modules", icon: BookOpen },
    { number: "23", label: "Practical Tools", icon: Target },
    { number: "Daily", label: "Relationship Insights", icon: Sparkles }
  ];

  const assessments = [
    {
      title: "Relationship Health Check",
      description: "Discover your relationship's strengths and opportunities for growth across connection, communication, and conflict. Celebrate what's working and identify where skills could help",
      color: "from-pink-500 to-rose-500",
      icon: Heart
    },
    {
      title: "Values Alignment",
      description: "Discover where you and your partner align on life priorities, family, money and lifestyle, and where meaningful conversations need to happen",
      color: "from-purple-500 to-indigo-500",
      icon: Compass
    },
    {
      title: "Communication Style",
      description: "Understand your natural communication patterns and learn to bridge the gap between how you talk and how your partner hears",
      color: "from-blue-500 to-cyan-500",
      icon: MessageSquare
    },
    {
      title: "Relationship Patterns",
      description: "Understand the cycles that shape your relationship: both the patterns that serve you well and the ones worth changing",
      color: "from-amber-500 to-orange-500",
      icon: TrendingUp
    }
  ];

  const modules = [
    {
      title: "Appreciation & Connection",
      subtitle: "The 5:1 ratio and why it matters",
      description: "Learn Gottman's magic ratio: couples who stayed together showed about five positive interactions for every negative one, even when they disagreed. Master the art of genuine appreciation.",
      image: "/images/mod01-foundations.png",
      color: "from-pink-500 to-rose-500"
    },
    {
      title: "Understanding Emotions",
      subtitle: "Move beyond 'fine' and 'stressed'",
      description: "Expand your emotional vocabulary, develop attunement, and learn to validate feelings (yours and theirs) even when you don't agree.",
      image: "/images/mod02-emotions.png",
      color: "from-purple-500 to-indigo-500"
    },
    {
      title: "Communication",
      subtitle: "It's not what you say. It's how they hear it",
      description: "Avoid the Four Horsemen (criticism, contempt, defensiveness, stonewalling) and build repair attempts that strengthen rather than damage connection.",
      image: "/images/mod03-communication.png",
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Conflict Resolution",
      subtitle: "Disagree productively, not destructively",
      description: "Recognise physiological flooding before it derails you, take effective breaks, and address perpetual problems without needing to solve them completely.",
      image: "/images/mod04-conflict.png",
      color: "from-amber-500 to-orange-500"
    },
    {
      title: "Intimacy & Vulnerability",
      subtitle: "Closeness through courage",
      description: "Navigate desire discrepancy, understand the five dimensions of intimacy, and practise well-paced vulnerability, drawing on Brené Brown's work.",
      image: "/images/mod05-intimacy.png",
      color: "from-red-500 to-pink-500"
    },
    {
      title: "Trust & Repair",
      subtitle: "Maintain and restore trust",
      description: "Build trustworthiness through consistent actions, repair inevitable ruptures skilfully, and understand what it takes to restore trust when it's been damaged.",
      image: "/images/mod06-trust.png",
      color: "from-teal-500 to-emerald-500"
    },
    {
      title: "Growing Together",
      subtitle: "Stay connected whilst you both change",
      description: "Align on shared meaning, navigate different growth rates, and build a relationship map that evolves with you.",
      image: "/images/mod07-growing.png",
      color: "from-green-500 to-lime-500"
    },
    {
      title: "Resilience",
      subtitle: "Weather the storms that will come",
      description: "Build stress-reducing rituals, develop a crisis response playbook, and maintain connection when life gets hard.",
      image: "/images/mod08-resilience.png",
      color: "from-violet-500 to-purple-500"
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: "Deepen Connection",
      description: "Cultivate genuine appreciation, emotional attunement, and the kind of intimacy that grows stronger over time. Master the 5:1 ratio that separates thriving couples from struggling ones.",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: MessageSquare,
      title: "Master Communication",
      description: "Express needs clearly, listen with presence, and navigate difficult conversations with skill. Learn repair attempts that strengthen connection rather than damage it.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Shield,
      title: "Build Conflict Resilience",
      description: "Turn disagreements into opportunities for understanding. Learn to disagree productively, recognise flooding before it happens, and address perpetual problems without endless arguments.",
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: Eye,
      title: "Understand Your Patterns",
      description: "Recognise the cycles that shape your relationship: the ones that serve you and the ones that don't. Gain clarity about what's working and what could work better.",
      color: "from-purple-500 to-indigo-500"
    },
    {
      icon: Lightbulb,
      title: "Align on What Matters",
      description: "Get clear on your shared values, priorities, and vision for the future. Discover where you naturally align and where intentional communication matters most.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: TrendingUp,
      title: "Grow Together Through Change",
      description: "Navigate life's inevitable changes without growing distant. Build shared meaning, maintain connection through transitions, and create resilience that lasts.",
      color: "from-teal-500 to-cyan-500"
    }
  ];

  const tools = [
    {
      title: "Appreciation Builder",
      description: "Practise Gottman's 5:1 ratio by tracking positive interactions and building genuine appreciation habits",
      icon: Heart,
      time: "5-10 min",
      color: "from-pink-500 to-rose-500"
    },
    {
      title: "Feeling Wheel",
      description: "Expand your emotional vocabulary beyond 'fine' and 'stressed' to articulate what you're actually feeling",
      icon: Brain,
      time: "10 min",
      color: "from-purple-500 to-indigo-500"
    },
    {
      title: "Four Horsemen Detector",
      description: "Spot criticism, contempt, defensiveness, and stonewalling before they derail your conversation",
      icon: Eye,
      time: "15 min",
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Flooding Monitor",
      description: "Track physiological arousal, recognise when you're flooded, and learn to take effective breaks",
      icon: BarChart2,
      time: "10 min",
      color: "from-amber-500 to-orange-500"
    },
    {
      title: "Repair Builder",
      description: "Craft repair attempts that actually work for your relationship: personalised, not prescriptive",
      icon: Sparkles,
      time: "15 min",
      color: "from-red-500 to-pink-500"
    },
    {
      title: "Vulnerability Coach",
      description: "Practise vulnerability with boundaries (courage, not oversharing), drawing on Brené Brown's work",
      icon: Shield,
      time: "20 min",
      color: "from-teal-500 to-emerald-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-24">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        {/* Animated Background Elements */}
        <motion.div
          style={{ y: y1 }}
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          style={{ y: y2 }}
          className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"
        />

        {/* Floating Art Images */}
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 0.1, x: 0 }}
          transition={{ duration: 1.5, delay: 0.5 }}
          style={{ y: y1 }}
          className="absolute top-40 left-0 w-64 h-64 opacity-10 pointer-events-none hidden lg:block"
        >
          <img src="/images/mod01-foundations.png" alt="" className="w-full h-full object-cover rounded-3xl rotate-12" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 0.1, x: 0 }}
          transition={{ duration: 1.5, delay: 0.7 }}
          style={{ y: y2 }}
          className="absolute bottom-40 right-0 w-72 h-72 opacity-10 pointer-events-none hidden lg:block"
        >
          <img src="/images/mod05-intimacy.png" alt="" className="w-full h-full object-cover rounded-3xl -rotate-12" />
        </motion.div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <img
                src="/relationship-compass-logo.png"
                alt="Relationship Compass"
                className="w-32 h-32 md:w-40 md:h-40 mx-auto object-contain"
              />
            </motion.div>

            {/* Tagline */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full mb-8"
            >
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm font-medium">
                From confused to clear • From conflict to connection
              </span>
            </motion.div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight px-4">
              Master the Art of
              <span className="block gradient-text">
                Relationship Success
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto px-4">
              Some couples seem to navigate everything with ease. What's their secret? It turns out there's no one route to success. But there are essential navigation skills every relationship needs. Discover the proven communication techniques, conflict patterns, and connection habits that help you chart your course together. That's what this compass is for.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => navigate('/assessment')}
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold text-lg hover:scale-105 transition-transform shadow-lg shadow-purple-500/50"
              >
                Start Free Assessment
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => document.getElementById('modules-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-8 py-4 bg-slate-700/50 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-slate-700 transition-colors border border-slate-600"
              >
                Explore Modules
              </button>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap justify-center gap-6 text-sm text-slate-400"
            >
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                No credit card required
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Short assessments
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Evidence-based approach
              </span>
            </motion.div>
          </motion.div>

          {/* Hero Journey Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-16 max-w-4xl mx-auto"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 blur-3xl opacity-20" />
              <div className="relative p-8 bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700">
                <div className="grid md:grid-cols-4 gap-6">
                  {[
                    { step: 'Assess', desc: 'Discover your patterns' },
                    { step: 'Learn', desc: 'Build practical skills' },
                    { step: 'Practise', desc: 'Use practical tools' },
                    { step: 'Grow', desc: 'Navigate together' }
                  ].map((item, idx) => (
                    <motion.div
                      key={item.step}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 + idx * 0.1 }}
                      className="text-center"
                    >
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl font-bold text-white">{idx + 1}</span>
                      </div>
                      <h3 className="text-white font-semibold mb-2">{item.step}</h3>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-slate-800/50 backdrop-blur-xl">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {realStats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <Icon className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <div className="text-3xl font-bold text-white">{stat.number}</div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What Is Relationship Compass */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative p-8 md:p-12 bg-slate-800/50 backdrop-blur-xl rounded-3xl border border-slate-700">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-3xl" />
              <div className="relative text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full mb-6">
                  <Compass className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-300 text-sm font-medium">How it works</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Your Interactive Guide to Better Relationships
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  Relationship Compass is an interactive learning app that helps you understand your relationship patterns,
                  build practical communication skills, and navigate conflicts with confidence. Take assessments to discover
                  where you are, work through research-informed modules to learn practical techniques, then use daily tools
                  to put those skills into practice, right in the moments that matter.
                </p>
                <div className="grid md:grid-cols-3 gap-6 text-left">
                  <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                    <Brain className="w-8 h-8 text-purple-400 mb-3" />
                    <h3 className="text-white font-semibold mb-2">Self-Guided Learning</h3>
                    <p className="text-sm text-slate-400">Not therapy or coaching: practical skills you develop at your own pace</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                    <Target className="w-8 h-8 text-pink-400 mb-3" />
                    <h3 className="text-white font-semibold mb-2">Action-Oriented</h3>
                    <p className="text-sm text-slate-400">Tools you can use today, in real conversations, not just theory</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                    <BookOpen className="w-8 h-8 text-indigo-400 mb-3" />
                    <h3 className="text-white font-semibold mb-2">Evidence-Based</h3>
                    <p className="text-sm text-slate-400">Techniques drawn from decades of relationship research, not trendy advice</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Gain */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 px-4">
              What You'll Actually
              <span className="gradient-text"> Achieve</span>
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Not fluffy 'just communicate better' advice. Real, research-backed skills you can
              actually use, like tonight. The kind that help you understand why you're both
              frustrated, say what you actually mean, and turn conflict into connection.
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:scale-105 transition-transform h-full">
                    <div className={`w-14 h-14 bg-gradient-to-br ${benefit.color} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                    <p className="text-slate-300 leading-relaxed">{benefit.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Assessments Section */}
      <section className="py-20 bg-slate-800/30 relative overflow-hidden">
        {/* Subtle background patterns */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64">
            <img src="/images/mod02-emotions.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64">
            <img src="/images/mod03-communication.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 px-4">
              Four Assessments to
              <span className="gradient-text"> Know Your Relationship</span>
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              No judgement. No labels. Just clarity about your strengths, your patterns, and where
              you can grow together. Take them solo or share with your partner for deeper insights.
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            {assessments.map((assessment, idx) => {
              const Icon = assessment.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="p-6 sm:p-8 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:scale-105 transition-transform h-full">
                    <div className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${assessment.color} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{assessment.title}</h3>
                    <p className="text-sm sm:text-base text-slate-300 leading-relaxed">{assessment.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => navigate('/assessment')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold text-lg hover:scale-105 transition-transform shadow-lg shadow-purple-500/50"
            >
              Take Your First Assessment
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>

          {/* Partner Sharing Callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-4xl mx-auto"
          >
            <div className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-8 h-8 text-purple-400" />
                <h3 className="text-2xl font-bold text-white">Take Assessments Together</h3>
              </div>
              <p className="text-slate-300 mb-4">
                Want to compare your results with your partner? Our Values Alignment assessment
                includes partner sharing: take it on separate devices, then see your results side-by-side.
              </p>
              <p className="text-sm text-slate-400">
                Share via QR code or a simple session code • Real-time sync • See where you align and where you differ
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modules-section" className="py-20 relative overflow-hidden">
        {/* Decorative background art */}
        <div className="absolute inset-0 overflow-hidden opacity-5 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-96 h-96">
            <img src="/images/mod04-conflict.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
          <div className="absolute -bottom-20 -right-20 w-96 h-96">
            <img src="/images/mod06-trust.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 px-4">
              Eight Modules to
              <span className="gradient-text"> Build Lasting Resilience</span>
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-3 px-4">
              From decades of relationship research on what makes couples thrive to evidence-based
              vulnerability practices: learn the skills that strengthen connection and create resilience for whatever life brings.
            </p>
            <p className="text-base sm:text-lg text-purple-300 max-w-2xl mx-auto italic px-4">
              Part of the Dive Deep Dialogues mission: deeper dialogues build resilience
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {modules.map((module, idx) => {
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:scale-105 transition-transform h-full overflow-hidden">
                    {/* Module Image */}
                    <div className="relative h-40">
                      <img
                        src={module.image}
                        alt={module.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
                      {/* Module number badge */}
                      <div className="absolute top-3 left-3 w-8 h-8 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                        <span className="text-white text-sm font-bold">{idx + 1}</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2">{module.title}</h3>
                      <p className="text-purple-300 text-sm mb-3 italic">{module.subtitle}</p>
                      <p className="text-slate-300 leading-relaxed">{module.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold text-lg hover:scale-105 transition-transform shadow-lg shadow-purple-500/50"
            >
              Explore All Modules
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>

          {/* Research Insight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-4xl mx-auto"
          >
            <div className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl">
              <Brain className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <div className="text-center">
                <p className="text-2xl text-white font-medium mb-4">
                  In John Gottman's long-term studies, couples who stayed together kept about five positive
                  interactions for every negative one, even while disagreeing. Contempt was the strongest warning sign.
                </p>
                <footer className="text-slate-400">John Gottman's research on couples in conflict (Gottman, 1993, 1994)</footer>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Practical Tools Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900/10 to-slate-900 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-1/4 left-0 w-72 h-72">
            <img src="/images/mod07-growing.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
          <div className="absolute bottom-1/4 right-0 w-72 h-72">
            <img src="/images/mod08-resilience.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 px-4">
              23 Practical Tools to
              <span className="gradient-text"> Apply What You Learn</span>
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Relationships don't improve through reading alone. They improve through practice.
              Each tool gives you concrete ways to build new habits and break old patterns.
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, idx) => {
              const Icon = tool.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:scale-105 transition-transform h-full group cursor-pointer">
                    <div className={`w-14 h-14 bg-gradient-to-br ${tool.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{tool.title}</h3>
                    <p className="text-sm text-slate-300 mb-3 leading-relaxed">{tool.description}</p>
                    <div className="flex items-center gap-2 text-xs text-purple-400">
                      <Clock className="w-3 h-3" />
                      <span>{tool.time}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => navigate('/toolkit')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-slate-700/50 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-slate-700 transition-colors border border-slate-600"
            >
              <Zap className="w-5 h-5" />
              Explore All Tools
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Why This Approach Works */}
      <section className="py-20 bg-slate-800/30 relative overflow-hidden">
        {/* Subtle background patterns */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-0 right-1/3 w-64 h-64">
            <img src="/images/assessment.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
          <div className="absolute bottom-0 left-1/3 w-64 h-64">
            <img src="/images/toolkit.png" alt="" className="w-full h-full object-cover rounded-full blur-sm" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 px-4">
              Why This Approach
              <span className="gradient-text"> Actually Works</span>
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Not generic advice. Not one-size-fits-all templates. Real skills based on the findings of decades of research.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 h-full">
                <Sparkles className="w-10 h-10 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  Actually Research-Backed
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Every module draws on published relationship research and clinical practice, including studies on communication patterns, emotional attunement, and vulnerability. Where an idea is our own, we say so.
                  You're learning what the evidence supports, not just what sounds nice.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 h-full">
                <Target className="w-10 h-10 text-pink-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  Personalised to Your Patterns
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Assessments reveal your specific patterns, communication style, and areas for growth.
                  Focus on what matters most to your relationship, not generic advice.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 h-full">
                <Heart className="w-10 h-10 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  Partner-Focussed, Not Just Self-Help
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Share assessments with your partner, compare results, and use tools together.
                  Because relationship skills work best when you're both invested.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 h-full">
                <Coffee className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">
                  Fits Your Actual Life
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  Modules you can work through a section at a time, 5-10 minute tools. No three-hour courses or homework you'll never do.
                  Real change in the time you actually have.
                </p>
              </div>
            </motion.div>
          </div>

          {/* DDD Mission Callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-12 max-w-4xl mx-auto"
          >
            <div className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl">
              <div className="flex items-center gap-3 mb-4 justify-center">
                <MessageSquare className="w-8 h-8 text-purple-400" />
                <h3 className="text-2xl font-bold text-white">The Dive Deep Dialogues Mission</h3>
              </div>
              <p className="text-slate-200 text-center text-lg mb-2">
                <span className="text-purple-300 font-semibold">Deeper dialogues build resilience.</span>
              </p>
              <p className="text-slate-300 text-center max-w-2xl mx-auto">
                Whether it's relationships, workplace dynamics, or inner conversations, meaningful dialogue turns understanding into strength. Relationship Compass applies this philosophy to your most important connection.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 px-4">
              Common Questions
              <span className="gradient-text"> We Get</span>
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: "Is this for relationships that are struggling or relationships that are strong?",
                a: "Both, and everything in between. The best time to build resilience is before you need it. Strong relationships get stronger with these skills, and struggling ones find pathways forward."
              },
              {
                q: "What if my partner won't do this with me?",
                a: "You can still gain significant value solo. Understanding your own patterns, communication style, and needs creates change, even if your partner isn't actively participating yet."
              },
              {
                q: "How is this different from couples therapy?",
                a: "This is proactive skills training, not therapy. Think of it like fitness training for your relationship: building strength and resilience using the same research that informs therapy, but focussed on growth and prevention."
              },
              {
                q: "How much time does it actually take?",
                a: "Assessments take 10-25 minutes each. Each module takes about 50-55 minutes in total, split into sections you can do separately. Tools are 5-10 minutes. You can go at your own pace: binge it or spread it over weeks."
              },
              {
                q: "Is this right for us if I feel afraid of my partner?",
                a: "No. Please put your safety first. The skills here assume two people who feel safe with each other, and couples counselling isn't recommended where there's abuse. If your partner frightens, controls, threatens or hurts you, contact a family violence service (1800RESPECT, 1800 737 732, in Australia; the Resources page lists services for each country this app serves), and if you're in danger, call your local emergency number."
              },
              {
                q: "What if we're incompatible? Will this tell us?",
                a: "The Values Alignment assessment specifically explores compatibility on life priorities, family, money, and lifestyle. Sometimes the answer is clarity about fundamental incompatibility, and that's valuable too."
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    {faq.q}
                  </h3>
                  <p className="text-slate-300 ml-8">{faq.a}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 blur-3xl" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="p-12 bg-gradient-to-br from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/20 text-center">
              <Gift className="w-16 h-16 text-yellow-400 mx-auto mb-6" />

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 px-4">
                Ready to Build Relationship Skills
                <span className="gradient-text"> That Last</span>?
              </h2>

              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-2xl mx-auto px-4">
                Start with a free assessment. No credit card. No spam. No pressure.
                Just clarity about your strengths and practical skills to keep growing together.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <button
                  onClick={() => navigate('/assessment')}
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold text-lg hover:scale-105 transition-transform shadow-lg shadow-purple-500/50"
                >
                  <Sparkles className="w-5 h-5" />
                  Start Your Free Assessment
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              <p className="text-sm text-slate-400">
                4 assessments • 8 modules • 23 tools • Learn at your own pace
              </p>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
